import type { jsPDF as JsPdfDocument } from 'jspdf';
import { calculatePageSlices } from './pdfPagination';
import { waitForCloudDocumentAssets } from './cloudDocumentAssets';

export interface BillingPdfResult {
  pdf: JsPdfDocument;
  blob: Blob;
  pageCount: number;
}

export const createBillingPdf = async (documentElement: HTMLElement, documentNumber: string): Promise<BillingPdfResult> => {
  const alreadyExportReady = documentElement.classList.contains('pdf-export');
  if (!alreadyExportReady) documentElement.classList.add('pdf-export');
  try {
    await waitForCloudDocumentAssets(documentElement);
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    await document.fonts.ready;
    await Promise.all([...documentElement.querySelectorAll('img')].map((image) => image.complete
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
          image.addEventListener('load', () => resolve(), { once: true });
          image.addEventListener('error', () => resolve(), { once: true });
        })));
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
    const canvas = await html2canvas(documentElement, { scale: 3, useCORS: true, backgroundColor: '#ffffff', logging: false });
    const pageWidthMm = 210;
    const pageHeightMm = 297;
    const pageHeightPx = Math.floor(canvas.width * pageHeightMm / pageWidthMm);
    const documentRect = documentElement.getBoundingClientRect();
    const pixelScale = canvas.width / documentRect.width;
    const relativeTop = (element: Element) => Math.max(0, Math.round((element.getBoundingClientRect().top - documentRect.top) * pixelScale));
    const relativeBottom = (element: Element) => Math.min(canvas.height, Math.round((element.getBoundingClientRect().bottom - documentRect.top) * pixelScale));
    const table = documentElement.querySelector('.document-table');
    const tableHead = table?.querySelector('thead');
    const tableRows = [...(table?.querySelectorAll('tbody tr') ?? [])];
    const finalBlock = documentElement.querySelector('.billing-final-block');
    const footer = documentElement.querySelector('.document-footer');
    const contentEnd = finalBlock ? relativeBottom(finalBlock) : footer ? relativeBottom(footer) : canvas.height;
    const headerTop = tableHead ? relativeTop(tableHead) : 0;
    const headerHeight = tableHead ? Math.max(1, relativeBottom(tableHead) - headerTop) : 0;
    const tableBodyStart = tableRows[0] ? relativeTop(tableRows[0]) : Number.POSITIVE_INFINITY;
    const tableEnd = table ? relativeBottom(table) : 0;
    const continuationTopSpace = Math.round(canvas.width * 12 / pageWidthMm);
    const pageNumberSpace = Math.round(canvas.width * 14 / pageWidthMm);
    const pagination = calculatePageSlices({
      contentEnd,
      pageHeight: pageHeightPx,
      continuationTopSpace,
      pageNumberSpace,
      tableBodyStart,
      tableEnd,
      headerHeight,
      rowBreaks: tableRows.map(relativeBottom),
      safeBreaks: [tableBodyStart, tableEnd],
      finalBlock: finalBlock ? { start: relativeTop(finalBlock), end: relativeBottom(finalBlock) } : null,
    });
    if (pagination.oversizedFinalBlock) throw new Error('ส่วนสรุปยอดยาวเกินหนึ่งหน้า กรุณาลดข้อความก่อนสร้าง PDF');

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    pagination.slices.forEach((slice, pageIndex) => {
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = pageHeightPx;
      const context = pageCanvas.getContext('2d');
      if (!context) throw new Error('ไม่สามารถเตรียมหน้ากระดาษได้');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      let destinationY = pageIndex > 0 ? continuationTopSpace : 0;
      if (slice.repeatHeader && tableHead) {
        context.drawImage(canvas, 0, headerTop, canvas.width, headerHeight, 0, destinationY, canvas.width, headerHeight);
        destinationY += headerHeight;
      }
      context.drawImage(canvas, 0, slice.start, canvas.width, slice.end - slice.start, 0, destinationY, canvas.width, slice.end - slice.start);
      context.fillStyle = '#596157';
      context.font = `${Math.round(8 * pixelScale)}px "IBM Plex Sans Thai", sans-serif`;
      const footerY = pageCanvas.height - Math.round(canvas.width * 7 / pageWidthMm);
      const footerInset = Math.round(canvas.width * 14 / pageWidthMm);
      context.textAlign = 'left';
      context.fillText(documentNumber, footerInset, footerY);
      context.textAlign = 'right';
      context.fillText(`หน้า ${pageIndex + 1} / ${pagination.slices.length}`, pageCanvas.width - footerInset, footerY);
      if (pageIndex > 0) pdf.addPage('a4', 'portrait');
      pdf.addImage(pageCanvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidthMm, pageHeightMm, undefined, 'FAST');
    });
    return { pdf, blob: pdf.output('blob'), pageCount: pagination.slices.length };
  } finally {
    if (!alreadyExportReady) documentElement.classList.remove('pdf-export');
  }
};

export const billingPdfFilename = (documentNumber: string) => `${documentNumber.replace(/[^a-zA-Z0-9ก-๙._-]/g, '-')}.pdf`;
